import {
  Alert,
  Button,
  List,
  Stack,
  Switch,
  Text,
  Textarea,
} from '@mantine/core'
import { DateInput } from '@mantine/dates'
import { IconInfoCircle, IconSparkles } from '@tabler/icons-react'
import { useNavigate } from '@tanstack/react-router'
import { AttachmentField } from '$src/components/common/AttachmentField'
import { useAttachments } from '$src/lib/hooks/useAttachments'
import { propertyHooks } from '$src/lib/trpcHooks'
import { SystemTaskFormData } from './types'

interface SystemTaskFormProps {
  data: SystemTaskFormData
  onChange: (data: SystemTaskFormData) => void
  markAsCompleted: boolean
  onMarkAsCompletedChange: (value: boolean) => void
  disabled?: boolean
  isMarkkuTask?: boolean
  hasPackage: boolean
  attachments: ReturnType<typeof useAttachments>
}

export function SystemTaskForm({
  data,
  onChange,
  markAsCompleted,
  onMarkAsCompletedChange,
  disabled,
  isMarkkuTask = false,
  hasPackage,
  attachments,
}: SystemTaskFormProps) {
  const navigate = useNavigate()
  const { data: propertiesResponse } = propertyHooks.useList()

  const handleFieldChange = (field: keyof SystemTaskFormData, value: any) => {
    onChange({ ...data, [field]: value })
  }

  // If this is a Markku task, show informational content or upsell
  if (isMarkkuTask) {
    // Show upsell if no package
    if (!hasPackage) {
      const firstProperty = propertiesResponse?.data[0]

      return (
        <Stack gap="md">
          <Alert
            icon={<IconSparkles size={20} />}
            title="Osa Markku-pakettia"
            color="orange"
          >
            <Stack gap="md">
              <Text>
                Tämä huoltotehtävä kuuluu Markku-pakettiin. Kun hankitset
                paketin, ammattilaiset hoitavat tämän tehtävän puolestasi.
              </Text>

              <List
                spacing="xs"
                size="sm"
                icon={
                  <IconSparkles
                    size={16}
                    color="var(--mantine-color-brandOrange-6)"
                  />
                }
              >
                <List.Item>Säännölliset huollot - ei yllätyksiä</List.Item>
                <List.Item>Ammattilaiset hoitavat tehtävät</List.Item>
                <List.Item>Mielenrauha ja ajansäästö</List.Item>
                <List.Item>Kiinteistösi arvo säilyy</List.Item>
              </List>

              <Button
                color="brandOrange"
                onClick={() => {
                  if (firstProperty) {
                    navigate({
                      to: '/property/$propertyId',
                      params: { propertyId: String(firstProperty.id) },
                      search: { tab: 'package' },
                    })
                  }
                }}
              >
                Tutustu Markku-paketteihin
              </Button>
            </Stack>
          </Alert>
        </Stack>
      )
    }

    // Show regular info if has package
    return (
      <Stack gap="md">
        <Alert
          icon={<IconInfoCircle size={20} />}
          title="Markku hoitaa tämän tehtävän"
          color="blue"
        >
          <Stack gap="xs">
            <Text>
              Tämä huoltotehtävä kuuluu Markku-palvelusopimukseesi. Markkun
              ammattilaiset hoitavat tehtävän puolestasi sovitun aikataulun
              mukaisesti.
            </Text>
            <Text>
              Jos sinulla on kysyttävää tästä tehtävästä tai havaitset ongelman,
              ota yhteyttä asiakaspalveluumme.
            </Text>
          </Stack>
        </Alert>
      </Stack>
    )
  }

  // For user tasks, show the regular form
  return (
    <Stack gap="md">
      <Switch
        label="Merkitse tehdyksi"
        checked={markAsCompleted}
        onChange={(event) =>
          onMarkAsCompletedChange(event.currentTarget.checked)
        }
        disabled={disabled}
        size="md"
      />

      {markAsCompleted && (
        <DateInput
          label="Työn suorittamisen päivämäärä"
          placeholder="Valitse päivämäärä"
          value={data.completedDate ? new Date(data.completedDate) : new Date()}
          onChange={(value) => {
            handleFieldChange('completedDate', value || '')
          }}
          required
          disabled={disabled}
        />
      )}

      <Textarea
        label="Muistiinpanot tehtävästä (valinnainen)"
        placeholder="Lisää muistiinpanoja tehtävästä..."
        value={data.notes}
        onChange={(e) => handleFieldChange('notes', e.currentTarget.value)}
        minRows={3}
        disabled={disabled}
      />

      <AttachmentField
        attachments={attachments.attachments}
        isUploading={attachments.isUploading}
        uploadFile={attachments.uploadFile}
        removeAttachment={attachments.removeAttachment}
        disabled={disabled}
      />
    </Stack>
  )
}
